import { Card, Typography, Button, Space, Tooltip } from "antd";
import { Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
  Title as ChartTitle,
} from "chart.js";
import { ExpandOutlined, PictureOutlined, FileExcelOutlined } from "@ant-design/icons";
import { useState, useRef } from "react";
import dayjs from "dayjs";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, ChartTooltip, Legend, ChartDataLabels, ChartTitle);

const { Title } = Typography;

export function EncuestasPorDiaChart({ data = [] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const chartRef = useRef(null);

  if (!data || data.length === 0) {
    return (
      <Card hoverable style={{ borderRadius: 12 }}>
        <Title level={5}>Encuestas por Día</Title>
        <p>No hay datos disponibles.</p>
      </Card>
    );
  }

  const totalGeneral = data.reduce((s, d) => s + d.total_encuestas, 0);
  const maxDia = data.reduce((a, b) => (a.total_encuestas > b.total_encuestas ? a : b));

  const chartData = {
    labels: data.map((d) => dayjs(d.fecha).format("DD/MM")),
    datasets: [
      {
        label: "Encuestas",
        data: data.map((d) => d.total_encuestas),
        borderColor: "#024589",
        backgroundColor: "rgba(2,69,137,0.12)",
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: "#024589",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (ctx) => dayjs(data[ctx[0].dataIndex].fecha).format("DD/MM/YYYY"),
          label: (ctx) => ` ${ctx.parsed.y.toLocaleString()} encuestas`,
        },
      },
      datalabels: {
        display: data.length <= 20,
        anchor: "end",
        align: "top",
        color: "#024589",
        font: { weight: "bold", size: 10 },
        formatter: (v) => (v === 0 ? "" : v.toLocaleString()),
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { font: { size: 11 }, maxRotation: 45 },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { callback: (v) => Number(v).toLocaleString() },
      },
    },
  };

  const exportImage = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const exp = document.createElement("canvas");
    const ctx = exp.getContext("2d");
    exp.width = canvas.width * 2;
    exp.height = canvas.height * 2;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, exp.width, exp.height);
    ctx.scale(2, 2);
    ctx.drawImage(canvas, 0, 0);
    const link = document.createElement("a");
    link.href = exp.toDataURL("image/png", 1.0);
    link.download = `encuestas_por_dia_${dayjs().format("YYYY-MM-DD")}.png`;
    link.click();
  };

  const exportCSV = () => {
    const rows = [
      ["Fecha", "Total Encuestas"],
      ...data.map((d) => [d.fecha, d.total_encuestas]),
      ["TOTAL", totalGeneral],
    ];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `encuestas_por_dia_${dayjs().format("YYYY-MM-DD")}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <Card
      hoverable
      style={{
        borderRadius: 12,
        ...(isExpanded && {
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1000, margin: 20, backgroundColor: "white",
        }),
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Encuestas Reportadas por Día</Title>
          <div style={{ fontSize: 14, color: "#666", marginTop: 4 }}>
            {`${totalGeneral.toLocaleString()} encuestas · pico: ${maxDia.total_encuestas.toLocaleString()} el ${dayjs(maxDia.fecha).format("DD/MM/YYYY")}`}
          </div>
        </div>
        <Space>
          <Tooltip title="Exportar imagen">
            <Button icon={<PictureOutlined />} onClick={exportImage} size="small" type="primary">Imagen</Button>
          </Tooltip>
          <Tooltip title="Exportar CSV">
            <Button icon={<FileExcelOutlined />} onClick={exportCSV} size="small">CSV</Button>
          </Tooltip>
          <Tooltip title={isExpanded ? "Minimizar" : "Maximizar"}>
            <Button icon={<ExpandOutlined />} onClick={() => setIsExpanded(!isExpanded)} size="small" type={isExpanded ? "primary" : "default"} />
          </Tooltip>
        </Space>
      </div>

      <div style={{ height: isExpanded ? "calc(100vh - 180px)" : 380, backgroundColor: "#fff" }}>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
    </Card>
  );
}
